import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HardHat, Plus, Pencil, UserX, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

export default function JobWorkers() {
  const [workers, setWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', specialization: '' });
  const [loading, setLoading] = useState(false);
  const [performance, setPerformance] = useState([]);

  const fetchWorkers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/job-workers?active_only=false`, { withCredentials: true });
      setWorkers(data);
    } catch {}
  }, []);

  const fetchPerformance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/wages/summary`, { withCredentials: true });
      setPerformance(data.worker_totals || []);
    } catch {}
  }, []);

  useEffect(() => { fetchWorkers(); fetchPerformance(); }, [fetchWorkers, fetchPerformance]);

  const openCreate = () => { setEditId(null); setForm({ name: '', phone: '', specialization: '' }); setModal(true); };
  const openEdit = (w) => { setEditId(w.id); setForm({ name: w.name, phone: w.phone || '', specialization: w.specialization || '' }); setModal(true); };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setLoading(true);
    try {
      if (editId) {
        await axios.put(`${API}/api/job-workers/${editId}`, form, { withCredentials: true });
        toast.success('Worker updated');
      } else {
        await axios.post(`${API}/api/job-workers`, form, { withCredentials: true });
        toast.success('Worker created');
      }
      setModal(false);
      fetchWorkers();
      fetchPerformance();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const deactivate = async (id, name) => {
    if (!window.confirm(`Deactivate ${name}?`)) return;
    try {
      await axios.delete(`${API}/api/job-workers/${id}`, { withCredentials: true });
      toast.success('Worker deactivated');
      fetchWorkers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/api/job-workers/template`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'job_workers_template.xlsx'); document.body.appendChild(link);
      link.click(); link.remove();
    } catch { toast.error('Failed to download template'); }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/job-workers/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(data.message);
      if (data.warnings) toast.warning(`Warnings: ${data.warnings.join(', ')}`);
      fetchWorkers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    e.target.value = '';
  };

  const filtered = workers.filter(w => !search || w.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="p-6" data-testid="job-workers-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Job Workers</h1>
          <p className="text-sm text-zinc-500">Manage workers and track their performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="h-9 rounded-sm text-xs" onClick={downloadTemplate} data-testid="download-workers-template">
            <Download size={12} className="mr-1" /> Template
          </Button>
          <label className="cursor-pointer">
            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleBulkUpload} data-testid="upload-workers-file" />
            <div className="flex items-center gap-1 h-9 px-3 text-xs border border-zinc-200 rounded-sm hover:bg-zinc-50 cursor-pointer">
              <Upload size={12} /> Upload
            </div>
          </label>
          <Button className="h-9 rounded-sm bg-[#09090B]" onClick={openCreate} data-testid="add-worker-btn">
            <Plus size={14} className="mr-2" /> Add Worker
          </Button>
        </div>
      </div>

      <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workers..." className="mb-4 h-9 rounded-sm max-w-sm" data-testid="worker-search" />

      {/* Workers + Performance Table */}
      <div className="bg-white border border-zinc-200">
        <div className="p-4 border-b border-zinc-200 flex items-center gap-2">
          <HardHat size={18} className="text-zinc-500" />
          <h2 className="font-heading font-bold text-lg">Workers & Performance</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="workers-table">
            <thead className="bg-zinc-50">
              <tr>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Name</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Phone</th>
                <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Specialization</th>
                <th className="text-center p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Status</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Input</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Total Output</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Loss</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Loss %</th>
                <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w, idx) => {
                const perf = performance.find(p => p.worker_id === w.id);
                return (
                  <tr key={w.id} className={`border-t border-zinc-200 ${idx % 2 === 1 ? 'bg-zinc-50/30' : ''} ${!w.active ? 'opacity-50' : ''}`} data-testid={`worker-row-${w.id}`}>
                    <td className="p-3 font-medium">{w.name}</td>
                    <td className="p-3 font-mono text-xs text-zinc-600">{w.phone || '-'}</td>
                    <td className="p-3 text-xs">{w.specialization || '-'}</td>
                    <td className="p-3 text-center">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${w.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                        {w.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono text-xs">{perf?.total_input?.toLocaleString() || '-'}</td>
                    <td className="p-3 text-right font-mono text-xs">{perf?.total_output?.toLocaleString() || '-'}</td>
                    <td className="p-3 text-right font-mono text-xs">{perf ? perf.total_loss : '-'}</td>
                    <td className="p-3 text-right">
                      {perf ? (
                        <span className={`font-mono text-xs font-bold ${perf.loss_percent > 2 ? 'text-red-600' : 'text-green-600'}`}>
                          {perf.loss_percent}%
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(w)} data-testid={`edit-worker-${w.id}`}>
                          <Pencil size={14} />
                        </Button>
                        {w.active && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deactivate(w.id, w.name)} data-testid={`deactivate-worker-${w.id}`}>
                            <UserX size={14} />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm" data-testid="worker-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Worker' : 'Add New Worker'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm" data-testid="worker-name-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Phone</Label>
              <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="mt-1 h-8 rounded-sm text-sm" data-testid="worker-phone-input" />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Specialization</Label>
              <Input value={form.specialization} onChange={e => setForm(p => ({ ...p, specialization: e.target.value }))} placeholder="e.g. Cutting, Stitching" className="mt-1 h-8 rounded-sm text-sm" data-testid="worker-specialization-input" />
            </div>
            <div className="flex justify-end gap-3 pt-2 border-t">
              <Button variant="outline" className="h-9 rounded-sm" onClick={() => setModal(false)}>Cancel</Button>
              <Button className="h-9 rounded-sm bg-[#09090B]" onClick={submit} disabled={loading} data-testid="submit-worker">
                {loading ? 'Saving...' : editId ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
